import { NotesPanel } from "./NotesPanel";
import { useNotes } from "../lib/notesContext";
import { overlaps, monthBounds } from "../lib/notes";
import { useMemo } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { ChartTooltip } from "./ChartTooltip";
import { userSeries, monthLabel } from "../lib/csvAnalysis";
import { fmtNum, fmtCompact } from "../lib/format";
import { monthlyWorked, userMonthly, toQuarters } from "../lib/pickerSheet";
import { MonthlyBreakdown } from "./MonthlyBreakdown";
import { QuarterCompare } from "./QuarterCompare";

const Stat = ({ label, value, testId, tone = "text-slate-100" }) => (
  <div className="rounded-lg bg-[#0B0F19] border border-slate-800 p-3">
    <p className="text-[10px] label-mono">{label}</p>
    <p className={`font-mono text-lg ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

export const UserDrilldown = ({ data, raw, cfg, userId, onClose, selected, onToggle }) => {
  const u = data.users.find((x) => x.id === userId);
  const worked = useMemo(() => monthlyWorked(raw?.days?.[userId]), [raw, userId]);
  const hasT = !!raw?.hasTime;
  const v = u ? userSeries(data.months, [u], "visits") : [];
  const q = u ? userSeries(data.months, [u], "qty") : [];
  const rows = v.map((r, i) => {
    const hrs = (worked[r.month] || 0) / 3600;
    return { label: r.label, month: r.month, visits: r[userId], qty: q[i][userId], hrs, vph: hrs ? r[userId] / hrs : null, qph: hrs ? q[i][userId] / hrs : null };
  });
  const monthly = useMemo(() => (u ? userMonthly(u, raw?.days?.[userId], data.months, cfg) : []), [u, raw, userId, data.months, cfg]);
  const quarters = useMemo(() => toQuarters(monthly), [monthly]);
  const { notes } = useNotes();
  const notedMonths = new Set(data.months.filter((m) => notes.some((n) => (n.picker === userId || n.scope === "team") && overlaps(n, monthBounds(m).start, monthBounds(m).end))));
  const totHrs = rows.reduce((s, r) => s + r.hrs, 0);
  return (
    <Sheet open={!!u} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="user-drilldown-modal" className="w-full sm:max-w-4xl bg-[#111827] border-slate-800 text-slate-200 overflow-y-auto">
        {u && <>
          <SheetHeader>
            <p className="label-mono">User deep dive</p>
            <SheetTitle className="font-display text-2xl text-slate-100" data-testid="drilldown-user-name">{u.id}</SheetTitle>
            <SheetDescription className="text-slate-500 font-mono text-xs">{monthLabel(u.first)} → {monthLabel(u.last)}</SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
            <Stat label="Visits" value={fmtNum(u.visits)} testId="drilldown-visits" />
            <Stat label="Qty picked" value={data.hasQty ? fmtNum(u.qty) : "—"} testId="drilldown-qty" />
            <Stat label="Avg qty / visit" value={data.hasQty ? fmtNum(u.avg) : "—"} />
            {hasT && <Stat label="Hours worked" value={fmtNum(totHrs)} testId="drilldown-hours" tone="text-amber-300" />}
            {hasT && <Stat label="Visits / hour" value={totHrs ? fmtNum(u.visits / totHrs) : "—"} testId="drilldown-vph" tone="text-ep-soft" />}
            {hasT && data.hasQty && <Stat label="Qty / hour" value={totHrs ? fmtNum(u.qty / totHrs) : "—"} testId="drilldown-qph" tone="text-emerald-300" />}
          </div>
          <div className="h-56 mt-6">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#1F2937" vertical={false} />
                <XAxis dataKey="label" stroke="#64748B" tick={{ fontSize: 10 }} tickLine={false} />
                <YAxis yAxisId="v" stroke="#D4333B" tickFormatter={fmtCompact} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                {data.hasQty && <YAxis yAxisId="q" orientation="right" stroke="#10B981" tickFormatter={fmtCompact} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />}
                <Tooltip content={<ChartTooltip />} />
                <Line yAxisId="v" dataKey="visits" name="Visits" stroke="#D4333B" strokeWidth={2} dot={false} />
                {data.hasQty && <Line yAxisId="q" dataKey="qty" name="Qty picked" stroke="#10B981" strokeWidth={2} dot={false} />}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <Button data-testid="drilldown-toggle-compare" onClick={() => onToggle(u.id)} className="mt-4 w-full bg-ep hover:bg-ep-hover text-white">
            {selected.includes(u.id) ? "Remove from comparison" : "Add to comparison"}
          </Button>
          <div className="mt-8"><NotesPanel title={`Notes about ${u.id}`} picker={u.id} includeTeam={false} defaults={{ scope: "picker", picker: u.id, period: "month", value: data.months[data.months.length - 1] }} pickers={data.users.map((x) => x.id)} testId="drilldown-notes" empty="No notes about this picker yet." /></div>
          <p className="label-mono mt-8 mb-2">Monthly breakdown · oldest → latest</p>
          <MonthlyBreakdown notedMonths={notedMonths} rows={monthly} hasQty={data.hasQty} hasT={hasT} />
          {hasT && <>
            <p className="label-mono mt-8 mb-2">Efficiency by quarter</p>
            <QuarterCompare quarters={quarters} hasQty={data.hasQty} />
          </>}
          {hasT && <p className="text-[10px] text-slate-500 mt-4">Lost time uses each day's detected shift (nearest to first pick) minus {cfg.breakMin} min lunch. Absent = Mon–Fri days with no picks between this picker's first and last active day.</p>}
        </>}
      </SheetContent>
    </Sheet>
  );
};
